// One issue: its fields edited in place, the evidence behind it (each piece one click from its conversation), how far
// it reaches, and the timeline — every change and every comment. A report shows by its reason only: what the user
// wrote is read on the conversation, an audited reading.
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { useSession } from '../../auth/session';
import { dateTime } from '../../services/format';
import { Alert, Badge, Button, Card, Field, Input, KeyValue, PageTitle, Select, Textarea } from '../../components/ui';
import type { FixRef, IssueDetail as Detail, IssueEvidence, IssueLinkKind, IssuePatch, IssueSeverity, IssueStatus, IssueTimelineItem } from '../../types/api';
import { OutcomeBadge, SeverityBadge, useLabel } from '../quality/common';
import { ISSUE_SEVERITIES, ISSUE_STATUSES, IssueSeverityBadge, IssueStatusBadge, isClosed } from './common';

const KINDS: IssueLinkKind[] = ['conversation', 'report', 'review', 'signal'];

function EvidenceLine({ e }: { e: IssueEvidence }) {
  const { t } = useTranslation();
  const reportReason = useLabel('reportReason');
  const signal = useLabel('signal');
  switch (e.kind) {
    case 'report':
      return <Badge tone="bad">{e.reason ? reportReason(e.reason) : '—'}</Badge>;
    case 'review':
      return (
        <span className="flex flex-wrap items-center gap-1">
          <OutcomeBadge outcome={e.outcome ?? null} />
          <SeverityBadge severity={e.severity ?? null} />
          {e.summary && <span className="text-[12px] text-ink">{e.summary}</span>}
        </span>
      );
    case 'signal':
      return (
        <span className="flex items-center gap-1">
          <Badge tone="warn">{e.signal_kind ? signal(e.signal_kind) : '—'}</Badge>
          {e.turn_index !== null && e.turn_index !== undefined && <span className="font-mono text-[11px] text-faint">{t('quality.turn', { index: e.turn_index })}</span>}
        </span>
      );
    default:
      return null;
  }
}

/** One change of the history: who made it, what changed — the value before and after, a long text folded — and, for
 *  the review, its reason. */
function TimelineEvent({ item }: { item: IssueTimelineItem }) {
  const { t, i18n } = useTranslation();
  const d = item.data ?? {};
  const kind = (k: unknown) => t(`issues.kind.${String(k)}`, String(k));
  const status = (v: unknown) => t(`issues.status.${String(v)}`, String(v));
  const severity = (v: unknown) => t(`issues.severity.${String(v)}`, String(v));
  const list = (v: unknown) => (Array.isArray(v) && v.length ? v.map((x) => (typeof x === 'string' ? x : (x as FixRef).label)).join(', ') : '—');
  const who = item.via === 'review' ? t('issues.via.review', { model: String(d.model ?? '') }) : item.via === 'report' ? t('issues.via.report')
    : (item.actor_email ?? '—');
  let text: ReactNode;
  let texts: [unknown, unknown] | null = null;
  switch (item.kind) {
    case 'created':
      text = t('issues.event.created', { severity: severity(d.severity) });
      break;
    case 'title':
    case 'description':
      text = t(`issues.event.${item.kind}`);
      texts = [d.from, d.to];
      break;
    case 'comment_edited':
      text = t('issues.event.commentEdited');
      texts = [d.from, d.to];
      break;
    case 'status':
      text = t('issues.event.status', { from: status(d.from), to: status(d.to) }) + (d.duplicate_of ? ` (#${String(d.duplicate_of)})` : '')
        + (d.regression ? ` — ${t('issues.regression')}` : '');
      break;
    case 'severity':
      text = t('issues.event.severity', { from: severity(d.from), to: severity(d.to) });
      break;
    case 'owner':
      text = t('issues.event.owner', { from: (d.from_email as string | null) ?? t('issues.nobody'), to: (d.to_email as string | null) ?? t('issues.nobody') });
      break;
    case 'labels':
    case 'fix_refs':
      text = t(`issues.event.${item.kind === 'labels' ? 'labels' : 'fixRefs'}`, { from: list(d.from), to: list(d.to) });
      break;
    case 'linked':
    case 'unlinked':
      text = t(`issues.event.${item.kind}`, { kind: kind(d.kind) });
      break;
    default:
      text = item.kind;
  }
  return (
    <li className="text-[12px] text-muted" data-testid={`event-${item.id}`}>
      <span className="font-mono text-[11px] text-faint">{dateTime(item.created_at, i18n.language)}</span>{' '}
      {item.via === 'review' ? <Badge tone="accent">{who}</Badge> : <span className="font-mono text-[11.5px] text-ink">{who}</span>} {text}
      {item.via === 'review' && typeof d.reason === 'string' && <p className="ml-4 mt-0.5 italic text-ink">“{d.reason}”</p>}
      {texts && (
        <details className="ml-4 mt-0.5">
          <summary className="cursor-pointer font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t('issues.beforeAfter')}</summary>
          <div className="mt-1 grid gap-2 md:grid-cols-2">
            {texts.map((v, k) => (
              <div key={k} className={`rounded-sm border px-2 py-1 ${k ? 'border-accent bg-accent-bg' : 'border-line-soft bg-code'}`}>
                <div className="font-mono text-[10px] uppercase text-faint">{t(k ? 'issues.after' : 'issues.before')}</div>
                <p className="whitespace-pre-wrap wrap-break-word text-ink">{v === null || v === undefined || v === '' ? '—' : String(v)}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </li>
  );
}

type Resolution = 'fixed' | 'wont_fix' | 'duplicate';
const RESOLUTIONS: Resolution[] = ['fixed', 'wont_fix', 'duplicate'];

/** How the issue ends: fixed (with the MR or release that fixed it), won't fix, or a duplicate of another issue; the
 *  note, when given, goes to the timeline as a comment. */
function ResolvePanel({ issue, busy, onResolve, onCancel }: { issue: Detail; busy: boolean; onCancel: () => void;
  onResolve: (patch: IssuePatch, note: string) => void }) {
  const { t } = useTranslation();
  const [how, setHow] = useState<Resolution>('fixed');
  const [duplicateOf, setDuplicateOf] = useState('');
  const [fix, setFix] = useState<FixRef>({ label: '', url: '' });
  const [note, setNote] = useState('');
  const withFix = how === 'fixed' && fix.label.trim() && fix.url.trim();
  const ready = how !== 'duplicate' || !!duplicateOf;
  const submit = () => onResolve({
    status: how,
    ...(how === 'duplicate' ? { duplicate_of: Number(duplicateOf) } : {}),
    ...(withFix ? { fix_refs: [...issue.fix_refs, { label: fix.label.trim(), url: fix.url.trim() }] } : {}),
  }, note);
  return (
    <Card title={t('issues.resolveTitle')} className="mb-4 border-accent">
      <div className="flex flex-col gap-3" data-testid="resolve-panel">
        <div className="grid gap-2 sm:grid-cols-3" role="radiogroup">
          {RESOLUTIONS.map((r) => (
            <label key={r} className={`flex cursor-pointer flex-col gap-0.5 rounded-md border px-3 py-2 ${how === r ? 'border-accent bg-accent-bg' : 'border-line-card hover:bg-hover-soft'}`}>
              <span className="flex items-center gap-2 text-[13px] font-medium text-ink">
                <input type="radio" name="resolution" value={r} checked={how === r} onChange={() => setHow(r)} data-testid={`resolution-${r}`} />
                {t(`issues.status.${r}`)}
              </span>
              <span className="text-[11.5px] text-muted">{t(`issues.resolutionHint.${r}`)}</span>
            </label>
          ))}
        </div>
        {how === 'fixed' && (
          <div className="grid gap-2 sm:grid-cols-2">
            <Field label={t('issues.fixedBy')} hint={t('issues.optional')}>
              <Input value={fix.label} onChange={(e) => setFix({ ...fix, label: e.target.value })} placeholder={t('issues.fixLabel')} data-testid="resolve-fix-label" />
            </Field>
            <Field label={t('issues.fixUrl')} hint={t('issues.optional')}>
              <Input value={fix.url} onChange={(e) => setFix({ ...fix, url: e.target.value })} placeholder="https://" data-testid="resolve-fix-url" />
            </Field>
          </div>
        )}
        {how === 'duplicate' && (
          <Field label={t('issues.duplicateOf')} className="w-40">
            <Input value={duplicateOf} onChange={(e) => setDuplicateOf(e.target.value.replace(/[^0-9]/g, ''))} placeholder="#" data-testid="resolve-duplicate-of" />
          </Field>
        )}
        <Field label={t('issues.resolveNote')} hint={t('issues.resolveNoteHint')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={t('issues.resolveNotePlaceholder')} data-testid="resolve-note" />
        </Field>
        <div className="flex gap-2">
          <Button variant="primary" busy={busy} disabled={!ready} onClick={submit} data-testid="confirm-resolve">
            {t('issues.resolveAs', { status: t(`issues.status.${how}`) })}
          </Button>
          <Button variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
      </div>
    </Card>
  );
}

/** Who closed the issue, when and how, from the last status event; with the way back. */
function ClosedBanner({ issue, busy, onReopen }: { issue: Detail; busy: boolean; onReopen: () => void }) {
  const { t, i18n } = useTranslation();
  const last = [...issue.timeline].reverse().find((e) => e.type === 'event' && e.kind === 'status' && e.data?.to === issue.status);
  const who = !last ? '—' : last.via === 'review' ? t('issues.via.review', { model: String(last.data?.model ?? '') }) : (last.actor_email ?? '—');
  const when = dateTime(issue.closed_at ?? last?.created_at, i18n.language);
  return (
    <div className="mb-4" data-testid="closed-banner">
      <Alert tone={issue.status === 'fixed' ? 'good' : 'warn'}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>
            <strong>{t(`issues.closedAs.${issue.status}`)}</strong> {t('issues.closedByLine', { who, when })}
            {issue.duplicate_of && (
              <> · <Link className="underline" to={`/issues/${issue.duplicate_of.id}`}>#{issue.duplicate_of.id} {issue.duplicate_of.title}</Link></>
            )}
            {issue.status === 'fixed' && issue.fix_refs.map((r, i) => (
              <span key={i}> · <a href={r.url} target="_blank" rel="noreferrer" className="underline">{r.label}</a></span>
            ))}
          </span>
          <Button busy={busy} onClick={onReopen} data-testid="reopen">{t('issues.reopen')}</Button>
        </div>
      </Alert>
    </div>
  );
}

function Comment({ item, issueId, mine, onSaved }: { item: IssueTimelineItem; issueId: number; mine: boolean; onSaved: (d: Detail) => void }) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await api.editIssueComment(issueId, item.id, draft ?? ''));
      setDraft(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-md border border-line-card bg-white px-3 py-2" data-testid={`comment-${item.id}`}>
      <div className="mb-1 flex items-center justify-between font-mono text-[11px] text-faint">
        <span>
          {item.actor_email ?? '—'} · {dateTime(item.created_at, i18n.language)}
          {item.edited_at && ` · ${t('issues.edited')}`}
        </span>
        {mine && draft === null && (
          <button type="button" className="underline" onClick={() => setDraft(item.body ?? '')} data-testid={`edit-comment-${item.id}`}>{t('common.edit')}</button>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      {draft === null ? (
        <p className="whitespace-pre-wrap wrap-break-word text-[13px] text-ink">{item.body}</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} data-testid={`edit-comment-body-${item.id}`} />
          <div className="flex gap-1.5">
            <Button variant="primary" busy={busy} disabled={!draft.trim()} onClick={() => void save()} data-testid={`save-comment-${item.id}`}>{t('common.save')}</Button>
            <Button variant="ghost" onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
          </div>
        </div>
      )}
    </li>
  );
}

export function IssueDetail() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const loaded = useAsync(() => api.issue(Number(id)), [id]);
  const staff = useAsync(() => api.users({ is_staff: true, page_size: 200 }), []);
  const [issue, setIssue] = useState<Detail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ title: string; description: string; labels: string } | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [fix, setFix] = useState<FixRef>({ label: '', url: '' });
  const [link, setLink] = useState<{ kind: IssueLinkKind; ref: string }>({ kind: 'conversation', ref: '' });
  const [comment, setComment] = useState('');
  const [resolving, setResolving] = useState(false);
  const [exporting, setExporting] = useState<'download' | 'copy' | null>(null);
  const [copied, setCopied] = useState(false);

  const d = issue && String(issue.id) === id ? issue : loaded.data;

  const run = async (call: () => Promise<Detail>) => {
    setBusy(true);
    setError(null);
    try {
      setIssue(await call());
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  };
  /** The Markdown export, saved as `issue-<id>.md` or copied to paste into Claude. Each conversation it carries is an
   *  audited export on the API side. */
  const exportFor = async (how: 'download' | 'copy') => {
    setExporting(how);
    setError(null);
    setCopied(false);
    try {
      const markdown = await api.issueMarkdown(Number(id));
      if (how === 'copy') {
        await navigator.clipboard.writeText(markdown);
        setCopied(true);
      } else {
        const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `issue-${id}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setExporting(null);
    }
  };
  const patch = (body: IssuePatch) => run(() => api.patchIssue(Number(id), body));
  /** The status first, so a refused one (`wrong_duplicate`) leaves no note behind; then the note as a comment. */
  const resolve = (body: IssuePatch, note: string) =>
    run(async () => {
      const closed = await api.patchIssue(Number(id), body);
      return note.trim() ? api.commentIssue(Number(id), note) : closed;
    }).then((ok) => ok && setResolving(false));

  if (loaded.error) return <Alert>{loaded.error}</Alert>;
  if (!d) return <p className="text-faint">{t('common.loading')}</p>;

  const setStatus = (status: IssueStatus) => {
    if (status === 'duplicate') setDuplicateOf('');
    else void patch({ status });
  };

  return (
    <div>
      <PageTitle
        title={
          <>
            <span className="font-mono text-faint">#{d.id}</span> {d.title}
          </>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-1.5">
            <IssueStatusBadge status={d.status} />
            <IssueSeverityBadge severity={d.severity} />
            {d.labels.map((l) => <Badge key={l}>{l}</Badge>)}
            <span className="font-mono text-[11px] text-faint">
              {d.source === 'staff' ? t('issues.openedBy', { who: d.created_by_email ?? '—' }) : t(`issues.openedVia.${d.source}`)} · {dateTime(d.created_at, i18n.language)}
            </span>
          </span>
        }
        actions={
          <>
            <Link to="/issues" className="text-[12.5px] text-muted underline">{t('issues.backToList')}</Link>
            <Button busy={exporting === 'copy'} onClick={() => void exportFor('copy')} title={t('issues.exportHelp')} data-testid="copy-for-claude">
              {copied ? t('issues.copied') : t('issues.copyForClaude')}
            </Button>
            <Button busy={exporting === 'download'} onClick={() => void exportFor('download')} title={t('issues.exportHelp')} data-testid="export-for-claude">
              {t('issues.exportForClaude')}
            </Button>
            {!isClosed(d.status) && !resolving && (
              <Button variant="primary" onClick={() => setResolving(true)} data-testid="resolve">{t('issues.resolve')}</Button>
            )}
            {!editing && (
              <Button onClick={() => setEditing({ title: d.title, description: d.description ?? '', labels: d.labels.join(', ') })} data-testid="edit-issue">
                {t('common.edit')}
              </Button>
            )}
          </>
        }
      />
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      {resolving && !isClosed(d.status) && <ResolvePanel issue={d} busy={busy} onResolve={(body, note) => void resolve(body, note)} onCancel={() => setResolving(false)} />}
      {isClosed(d.status) && <ClosedBanner issue={d} busy={busy} onReopen={() => void patch({ status: 'open' })} />}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="flex min-w-0 flex-col gap-4">
          {editing ? (
            <Card title={t('issues.editTitle')}>
              <div className="flex flex-col gap-2">
                <Field label={t('issues.titleLabel')}>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="edit-title" />
                </Field>
                <Field label={t('issues.descriptionLabel')}>
                  <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={6} data-testid="edit-description" />
                </Field>
                <Field label={t('issues.labelsLabel')} hint={t('issues.labelsHint')}>
                  <Input value={editing.labels} onChange={(e) => setEditing({ ...editing, labels: e.target.value })} data-testid="edit-labels" />
                </Field>
                <div className="flex gap-2">
                  <Button variant="primary" busy={busy} disabled={!editing.title.trim()} data-testid="save-issue"
                          onClick={() => void patch({ title: editing.title.trim(), description: editing.description.trim() || null,
                                                      labels: editing.labels.split(',').map((l) => l.trim()).filter(Boolean) }).then((ok) => ok && setEditing(null))}>
                    {t('common.save')}
                  </Button>
                  <Button variant="ghost" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card title={t('issues.descriptionLabel')}>
              {d.description ? <p className="whitespace-pre-wrap wrap-break-word text-[13px] text-ink" data-testid="description">{d.description}</p>
                : <p className="text-[12.5px] text-faint">{t('issues.noDescription')}</p>}
            </Card>
          )}

          <Card title={t('issues.evidenceTitle', { count: d.evidence.length })}>
            <div className="flex flex-col gap-3" data-testid="evidence">
              {KINDS.filter((k) => d.evidence.some((e) => e.kind === k)).map((k) => (
                <div key={k}>
                  <div className="mb-1 font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t(`issues.kindPlural.${k}`)}</div>
                  <ul className="flex flex-col gap-1.5">
                    {d.evidence.filter((e) => e.kind === k).map((e) => (
                      <li key={e.id} className="flex flex-wrap items-center gap-2 border-b border-line-soft pb-1.5 last:border-0" data-testid={`evidence-${e.kind}-${e.ref}`}>
                        {e.found ? (
                          <Link to={`/conversations/${e.thread_id}`} className="text-[12.5px] font-medium text-ink underline">{e.conversation_title || t('quality.untitled')}</Link>
                        ) : (
                          <span className="text-[12.5px] text-faint">{t('issues.notFound')}</span>
                        )}
                        <EvidenceLine e={e} />
                        <span className="font-mono text-[11px] text-faint">
                          {[e.user_email, e.organization_name, e.at ? dateTime(e.at, i18n.language) : null].filter(Boolean).join(' · ')}
                        </span>
                        <button type="button" className="ml-auto font-mono text-[11px] text-faint underline" disabled={busy}
                                onClick={() => void run(() => api.unlinkIssue(d.id, e.id))} data-testid={`unlink-${e.id}`}>
                          {t('issues.unlink')}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {!d.evidence.length && <p className="text-[12.5px] text-faint">{t('issues.noEvidence')}</p>}
              <div className="flex flex-wrap items-end gap-1.5 border-t border-line-soft pt-2">
                <Field label={t('issues.linkKind')}>
                  <Select value={link.kind} onChange={(e) => setLink({ ...link, kind: e.target.value as IssueLinkKind })}>
                    {KINDS.map((k) => <option key={k} value={k}>{t(`issues.kind.${k}`)}</option>)}
                  </Select>
                </Field>
                <Field label={t('issues.linkRef')} className="w-72">
                  <Input value={link.ref} onChange={(e) => setLink({ ...link, ref: e.target.value })} placeholder={t('issues.linkRefHint')} data-testid="link-ref" />
                </Field>
                <Button busy={busy} disabled={!link.ref.trim()} data-testid="add-link"
                        onClick={() => void run(() => api.linkIssue(d.id, { kind: link.kind, ref: link.ref.trim() })).then((ok) => ok && setLink({ ...link, ref: '' }))}>
                  {t('issues.addLink')}
                </Button>
              </div>
            </div>
          </Card>

          <Card title={t('issues.timeline')}>
            <ul className="flex flex-col gap-2" data-testid="timeline">
              {d.timeline.map((item) =>
                item.type === 'comment' ? (
                  <Comment key={`c-${item.id}`} item={item} issueId={d.id} mine={item.actor_id === me?.id} onSaved={setIssue} />
                ) : (
                  <TimelineEvent key={`e-${item.id}`} item={item} />
                ),
              )}
            </ul>
            <div className="mt-3 flex flex-col gap-1.5 border-t border-line-soft pt-3">
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder={t('issues.commentPlaceholder')} data-testid="comment-body" />
              <div>
                <Button variant="primary" busy={busy} disabled={!comment.trim()} data-testid="add-comment"
                        onClick={() => void run(() => api.commentIssue(d.id, comment)).then((ok) => ok && setComment(''))}>
                  {t('issues.comment')}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <Card title={t('issues.workflow')}>
            <div className="flex flex-col gap-2">
              <Field label={t('issues.statusLabel')}>
                <Select value={duplicateOf !== null ? 'duplicate' : d.status} onChange={(e) => setStatus(e.target.value as IssueStatus)} disabled={busy} data-testid="status">
                  {ISSUE_STATUSES.map((s) => <option key={s} value={s}>{t(`issues.status.${s}`)}</option>)}
                </Select>
              </Field>
              {duplicateOf !== null && (
                <div className="flex items-end gap-1.5">
                  <Field label={t('issues.duplicateOf')} className="w-32">
                    <Input value={duplicateOf} onChange={(e) => setDuplicateOf(e.target.value.replace(/[^0-9]/g, ''))} placeholder="#" data-testid="duplicate-of" />
                  </Field>
                  <Button busy={busy} disabled={!duplicateOf} data-testid="save-duplicate"
                          onClick={() => void patch({ status: 'duplicate', duplicate_of: Number(duplicateOf) }).then((ok) => ok && setDuplicateOf(null))}>
                    {t('common.save')}
                  </Button>
                  <Button variant="ghost" onClick={() => setDuplicateOf(null)}>{t('common.cancel')}</Button>
                </div>
              )}
              <Field label={t('issues.severityLabel')}>
                <Select value={d.severity} onChange={(e) => void patch({ severity: e.target.value as IssueSeverity })} disabled={busy} data-testid="severity">
                  {ISSUE_SEVERITIES.map((s) => <option key={s} value={s}>{t(`issues.severity.${s}`)}</option>)}
                </Select>
              </Field>
              <Field label={t('issues.owner')}>
                <Select value={d.owner_id ?? ''} onChange={(e) => void patch({ owner_id: e.target.value ? Number(e.target.value) : null })} disabled={busy} data-testid="owner">
                  <option value="">{t('issues.nobody')}</option>
                  {(staff.data?.items ?? []).map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
                  {d.owner_id && !(staff.data?.items ?? []).some((u) => u.id === d.owner_id) && <option value={d.owner_id}>{d.owner_email}</option>}
                </Select>
              </Field>
            </div>
          </Card>
          <Card title={t('issues.reach')}>
            <KeyValue
              rows={[
                [t('issues.users'), d.users],
                [t('issues.organizations'), d.organizations],
                [t('issues.lastSeen'), d.last_seen ? dateTime(d.last_seen, i18n.language) : '—'],
                [t('issues.updated'), dateTime(d.updated_at, i18n.language)],
                [t('issues.closed'), d.closed_at ? dateTime(d.closed_at, i18n.language) : '—'],
              ]}
            />
          </Card>
          <Card title={t('issues.fixRefs')}>
            <ul className="mb-2 flex flex-col gap-1" data-testid="fix-refs">
              {d.fix_refs.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-[12.5px]">
                  <a href={r.url} target="_blank" rel="noreferrer" className="truncate text-ink underline">{r.label}</a>
                  <button type="button" className="ml-auto font-mono text-[11px] text-faint underline" disabled={busy}
                          onClick={() => void patch({ fix_refs: d.fix_refs.filter((_, j) => j !== i) })}>{t('issues.remove')}</button>
                </li>
              ))}
              {!d.fix_refs.length && <li className="text-[12.5px] text-faint">—</li>}
            </ul>
            <div className="flex flex-col gap-1.5">
              <Input value={fix.label} onChange={(e) => setFix({ ...fix, label: e.target.value })} placeholder={t('issues.fixLabel')} data-testid="fix-label" />
              <Input value={fix.url} onChange={(e) => setFix({ ...fix, url: e.target.value })} placeholder="https://" data-testid="fix-url" />
              <div>
                <Button busy={busy} disabled={!fix.label.trim() || !fix.url.trim()} data-testid="add-fix"
                        onClick={() => void patch({ fix_refs: [...d.fix_refs, { label: fix.label.trim(), url: fix.url.trim() }] }).then((ok) => ok && setFix({ label: '', url: '' }))}>
                  {t('issues.addFix')}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
